import Button from '@/common/components/Button'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import useDebounce from '@/common/hooks/useDebounce'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { UserData } from '@/common/types/users'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import DeclineModal from '@/features/privateCompanyLeadsFunnel/components/DeclineModal'
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from '@/features/privateCompanyLeadsFunnel/schemas/update.schema'
import { LastReview, PrivateCompanyLeadData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead'
import { useUsers } from '@/features/users/hooks/useUsers'
import { UseMutateFunction } from '@tanstack/react-query'
import { BaseSyntheticEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

interface ResponsibleSectionProps {
  privateCompanyLead: PrivateCompanyLeadData
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  submitObservations?: (e?: BaseSyntheticEvent) => Promise<void>
  last_review?: LastReview
  approveLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  declineLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  disabled?: boolean
}

export const ResponsibleSection = ({
  privateCompanyLead,
  submit,
  submitObservations,
  last_review,
  approveLead,
  declineLead,
  disabled,
}: ResponsibleSectionProps) => {
  const [searchUsers, setSearchUsers] = useDebounce('')

  const { data: users, isPending: usersIsPending } = useUsers({
    filters: {
      search: searchUsers,
    },
    mapper: Mapper.mapToOptions<UserData>({ valueFieldName: 'id', labelFieldName: 'full_name' }),
  })

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<PrivateCompanyLeadsFunnelUpdateValidationSchema>()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const hasPermissionToUpdateManager = hasUserPermission('Admin::Funnel::PrivateCompanyLeads::UpdateManagers')
  const hasPermissionToUpdatePrivateCompanyLead = hasUserPermission('Admin::Funnel::PrivateCompanyLeads::Update')

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full justify-end">
        {approveLead && declineLead && (
          <>
            {last_review?.state === 'disapproved' ? (
              <div className="flex w-full items-center justify-between gap-2">
                <Button type="button" className="w-[164px] cursor-default" variant="error">
                  Declinado
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <DeclineModal submit={declineLead} />
                <Button
                  type="button"
                  className={twMerge('w-[164px]', last_review?.state === 'approved' && 'bg-success-disabled')}
                  onClick={() => approveLead({})}
                  disabled={last_review?.state === 'approved'}
                  name="approved"
                >
                  {last_review?.state === 'approved' ? 'Aprovado' : 'Aprovar'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {last_review?.state === 'disapproved' && <RequestedReview description={last_review?.description} />}
      <p className="text-[18px] font-bold">Responsáveis</p>

      <SelectController
        name="commercial_manager"
        control={control}
        label="Responsável interno"
        markAsRequired
        placeholder={(disabled || !hasPermissionToUpdateManager) ? '' : 'Selecione um responsável'}
        isLoading={usersIsPending || isUserPermissionsPending}
        options={users?.filter((user) => user.value !== privateCompanyLead.commercial_manager_substitute?.id)}
        onInputChange={setSearchUsers}
        triggerOnChange={submit}
        errorMessage={errors?.commercial_manager?.message}
        isDisabled={disabled || !hasPermissionToUpdateManager}
      />

      <SelectController
        name="commercial_manager_substitute"
        control={control}
        label="Responsável interno substituto"
        placeholder={(disabled || !hasPermissionToUpdateManager) ? '' : 'Selecione um responsável substituto'}
        className="w-full"
        isLoading={usersIsPending || isUserPermissionsPending}
        options={users?.filter((user) => user.value !== privateCompanyLead.commercial_manager?.id)}
        onInputChange={setSearchUsers}
        triggerOnChange={submit}
        isClearable
        errorMessage={errors?.commercial_manager_substitute?.message}
        isDisabled={disabled || !hasPermissionToUpdateManager}
      />

      <TextArea
        errorMessage={errors?.observations?.message}
        {...register('observations')}
        defaultValue={privateCompanyLead?.observations}
        rows={5}
        onBlur={submitObservations}
        disabled={disabled || !hasPermissionToUpdatePrivateCompanyLead}
      />
    </div>
  )
}
